/**
 * Framework-free toast queue. `toast()` can be called from anywhere (query
 * callbacks, SSE handlers); <ToastHost> subscribes and renders.
 */
export type ToastTone = 'neutral' | 'success' | 'warning' | 'danger' | 'info';

export interface ToastAction {
  label: string;
  onAction: () => void;
}

export interface ToastInput {
  title: string;
  description?: string | undefined;
  tone?: ToastTone;
  /** e.g. Undo for destructive or optimistic actions (BUILD-PLAN 3.3). */
  action?: ToastAction | undefined;
  /** ms; default 6000. Infinity keeps it until dismissed. */
  duration?: number;
  /** Re-using an id replaces that toast instead of stacking a duplicate. */
  id?: string;
}

export interface ToastRecord extends Required<Pick<ToastInput, 'title' | 'tone' | 'duration'>> {
  id: string;
  description?: string | undefined;
  action?: ToastAction | undefined;
  createdAt: number;
}

export const DEFAULT_TOAST_DURATION = 6000;
export const MAX_TOASTS = 4;

type Listener = (toasts: readonly ToastRecord[]) => void;

export function createToastStore(now: () => number = Date.now) {
  let toasts: ToastRecord[] = [];
  let seq = 0;
  const listeners = new Set<Listener>();
  const emit = () => {
    for (const l of listeners) l(toasts);
  };

  return {
    show(input: ToastInput): string {
      seq += 1;
      const id = input.id ?? `t${seq}`;
      const record: ToastRecord = {
        id,
        title: input.title,
        tone: input.tone ?? 'neutral',
        duration: input.duration ?? DEFAULT_TOAST_DURATION,
        createdAt: now(),
        ...(input.description !== undefined ? { description: input.description } : {}),
        ...(input.action !== undefined ? { action: input.action } : {}),
      };
      const without = toasts.filter((t) => t.id !== id);
      // Oldest drop off first once the stack is full.
      toasts = [...without, record].slice(-MAX_TOASTS);
      emit();
      return id;
    },
    dismiss(id?: string): void {
      toasts = id === undefined ? [] : toasts.filter((t) => t.id !== id);
      emit();
    },
    get(): readonly ToastRecord[] {
      return toasts;
    },
    subscribe(listener: Listener): () => void {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
}

export type ToastStore = ReturnType<typeof createToastStore>;
